import Content from "@/components/Content";
import { Box, Flex, Text, EmptyState, VStack, StatValueUnit, Input, SimpleGrid, Stack } from "@chakra-ui/react";
import SectionPanel from "@/components/SectionPanel";
import { format } from "date-fns";
import { deLocaleOption, formatDay, formatMinutes, formatPicker } from "@/data/DateTimeUtil";
import { useEffect, useState } from "react";
import { HiColorSwatch } from "react-icons/hi";
import { StatLabel, StatRoot, StatValueText } from "@/components/ui/stat"
import { Table } from "@chakra-ui/react"
import { ContractForm } from "./ContractForm";
import type { ContactFormData } from "./ContractForm";
import { WorkModelPreview } from "@/api/WorkModelPreview";
import { WorkModelsPreviewRequest } from "@/api/WorkModelsPreviewRequest";
import { workmodelService } from "@/service/WorkModelService";

import { useForm } from 'react-hook-form';
import { useLocationDefaults } from '@/hooks/useLocationDefaults';
import CountrySelect from '@/components/fields/CountrySelect';
import { Field } from '@/components/ui/field';
import { countries } from '@/data/Countries';
import { useI18n } from '@/i18n/I18nContext';

export const EmployeeWorkModel = () => {
  const [results, setResults] = useState<WorkModelPreview[]>([]);
  const { location } = useLocationDefaults();
  const { t, language } = useI18n();
  const { control, register, reset, watch, getValues, setValue } = useForm({
    values: location,
    resetOptions: { keepDirtyValues: true },
  });
  const country = watch('country');
  const city = watch('city');

  useEffect(() => {
    setResults([]);
  }, [country, city]);

  const onCancelInternal = () => {
    // reset form and results components
    setResults([]);
    reset(location, { keepDirtyValues: false });
  };

  // calculate based on intervals
  const onSubmitInternal = async (c: ContactFormData) => {
    if (!country) return;
    // calculate and update results component
    const request: WorkModelsPreviewRequest = {
      interval: c.interval,
      model: c.model,
      location: { country, city: city.trim() || null }
    };
    const res: WorkModelPreview[] = await workmodelService.preview(request);
    setResults(res);
  };

  return (
    <Stack maxW="5xl" mx="auto" gap="6" minW="0">
      <SectionPanel title={t('workModelPreview.locationTitle')}>
        <Text fontSize="xs" color="fg.muted">
          {t('workModelPreview.locationHint')}
        </Text>
        <SimpleGrid
          as="section"
          aria-label={t('signup.form.country') + ' / ' + t('signup.form.city')}
          gap="4"
          columns={{ base: 1, sm: 2 }}
          alignItems="center"
        >
          <CountrySelect
            control={control} name="country" readOnly={false}
            countryCodes={Object.keys(countries)}
            language={language} label={t('signup.form.country')} isClearable={false}
            onValueChange={value => {
              if (value !== getValues('country')) setValue('city', '', { shouldDirty: true });
            }}
          />
          <Field label={t('signup.form.city')}>
            <Input {...register('city')} autoComplete="address-level2" />
          </Field>
        </SimpleGrid>
      </SectionPanel>
      <SimpleGrid columns={{ base: 1, xl: 2 }} gap="6" minW="0">
        <SectionPanel>
          <ContractForm
            showInterval={true}
            submitDisabled={!country}
            submitCaption='Berechnen'
            onContractSubmit={onSubmitInternal}
            cancelCaption='Zurücksetzen'
            onContractCancel={onCancelInternal} />
        </SectionPanel>

        <SectionPanel title='Berechnung'>
          {results.length === 0 && (<NoResults />)}
          {results.length > 0 && (
            <Flex direction='column' gap='1rem'>
              {results.map((result, index) => (
                <Flex key={index} direction='column' gap='.3rem'>
                  <Flex direction='row' gap='.2rem' align='center'>
                    <Text fontSize='sm' fontWeight='semibold'>{formatPicker(result.interval.start)} - {formatPicker(result.interval.end)}</Text>
                  </Flex>
                  <SimpleGrid columns={{ base: 1, md: 3 }} gap='4' minW='0'>
                    <StatRoot>
                      <StatLabel>Ferienanspruch</StatLabel>
                      <StatValueText alignItems="baseline">{formatDay(result.vacations)}<StatValueUnit>d</StatValueUnit></StatValueText>
                    </StatRoot>
                    <StatRoot>
                      <StatLabel>Arbeitstage</StatLabel>
                      <StatValueText alignItems="baseline">{formatDay(result.months.map(w => w.workingDays).reduce((a, b) => a + b, 0))}<StatValueUnit>d</StatValueUnit></StatValueText>
                    </StatRoot>
                    <StatRoot>
                      <StatLabel>Sollstunden</StatLabel>
                      <StatValueText alignItems="baseline">{formatMinutes(result.months.map(w => w.workingMinutes).reduce((a, b) => a + b, 0))}<StatValueUnit>h</StatValueUnit></StatValueText>
                    </StatRoot>
                  </SimpleGrid>
                  <Box overflowX='auto' minW='0'>
                    <Table.Root size='sm'>
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader>Monate</Table.ColumnHeader>
                          <Table.ColumnHeader textAlign='end'>Tage</Table.ColumnHeader>
                          <Table.ColumnHeader textAlign='end'>Feiertage</Table.ColumnHeader>
                          <Table.ColumnHeader textAlign='end'>Arbeitstage</Table.ColumnHeader>
                          <Table.ColumnHeader textAlign='end'>Sollstunden</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {result.months.map((entry, index) => {
                          const dateMonth = new Date(result.interval.start);
                          dateMonth.setMonth(entry.month);
                          return (
                            <Table.Row key={index}>
                              <Table.Cell p='1'>{format(dateMonth, 'MMMM', deLocaleOption)}</Table.Cell>
                              <Table.Cell p='1' textAlign='end'>{entry.days}</Table.Cell>
                              <Table.Cell p='1' textAlign='end'>{entry.holidays}</Table.Cell>
                              <Table.Cell p='1' textAlign='end'>{entry.workingDays}</Table.Cell>
                              <Table.Cell p='1' textAlign='end'>{formatMinutes(entry.workingMinutes)}</Table.Cell>
                            </Table.Row>
                          )
                        })}
                      </Table.Body>
                    </Table.Root>
                  </Box>
                </Flex>
              ))}
            </Flex>
          )}
        </SectionPanel>
      </SimpleGrid>
    </Stack>
  );
}

const NoResults = () => {
  return (
    <EmptyState.Root>
      <EmptyState.Content>
        <EmptyState.Indicator>
          <HiColorSwatch />
        </EmptyState.Indicator>
        <VStack textAlign="center">
          <EmptyState.Title>Keine Ergebnisse</EmptyState.Title>
          <EmptyState.Description>
            Versuche, deine Suche anzupassen
          </EmptyState.Description>
        </VStack>
      </EmptyState.Content>
    </EmptyState.Root>
  )
}

const PreviewWorkModel = () => {

  return (
    <Content title='Arbeitszeit-Rechner 🧮' description='Arbeitszeit, Sollstunden und Anspruch von Ferientagen berechnen.'>
      <EmployeeWorkModel />
    </Content>
  );
}

export default PreviewWorkModel;
